import { useEffect, useRef, useState } from 'react';
import { ApiError, api, type CallieAction, type CallieProposal } from '../lib/api';
import { announceChange } from '../lib/changes';
import { canListen, listen, speak, stop as stopSpeaking, type SpeakState } from '../lib/voice';

/**
 * Callie (D34): a small chat panel, present on every page once signed in.
 * The person types what they want; the server-side agent turns it into the
 * platform's own actions under this login and answers. Each reply shows a
 * one-line "what I did" when a change was made, so nothing happens silently.
 */
interface Msg {
  who: 'me' | 'callie';
  text: string;
  actions?: CallieAction[];
  proposals?: CallieProposal[];
}

const OPEN_KEY = 'openmd.callie.open';
const VOICE_KEY = 'openmd.callie.voice';
const VOICE_NAME_KEY = 'openmd.callie.voice.name';

export function Callie({ abbreviation, editedBy, hint }: { abbreviation: string | null; editedBy: string | null; hint: string }) {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(() => read(OPEN_KEY) === '1');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [voice, setVoice] = useState(() => read(VOICE_KEY) !== '0'); // on unless switched off
  const [voices, setVoices] = useState<string[]>([]);
  const [voiceName, setVoiceName] = useState<string | null>(() => read(VOICE_NAME_KEY));
  const [speech, setSpeech] = useState<SpeakState>('idle');
  const [listening, setListening] = useState(false);
  const stopListening = useRef<(() => void) | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .callieConfig()
      .then((c) => {
        setEnabled(c.enabled);
        setVoices(c.voice?.enabled ? c.voice.voices : []);
      })
      .catch(() => setEnabled(false));
  }, []);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [msgs, open]);
  useEffect(() => {
    if (!open) {
      stopSpeaking();
      stopListening.current?.();
    }
    return () => {
      stopSpeaking();
      stopListening.current?.();
    };
  }, [open]);

  if (!enabled) return null;

  const toggle = () => {
    setOpen((o) => {
      write(OPEN_KEY, o ? null : '1');
      return !o;
    });
  };
  const send = async (e?: React.FormEvent, spoken?: string) => {
    e?.preventDefault();
    const message = (spoken ?? text).trim();
    if (!message || busy) return;
    setText('');
    stopSpeaking();
    setMsgs((m) => [...m, { who: 'me', text: message }]);
    setBusy(true);
    try {
      const r = await api.callie({ conversationId, message, abbreviation, editedBy });
      setConversationId(r.conversationId);
      setMsgs((m) => [...m, { who: 'callie', text: r.reply, actions: r.actions, proposals: r.proposals }]);
      const changed = r.actions.filter((a) => a.changed).map((a) => a.tool);
      if (changed.length) announceChange({ tools: changed });
      if (voice) void speak(r.reply, setSpeech, voiceName);
    } catch (err) {
      setMsgs((m) => [...m, { who: 'callie', text: err instanceof ApiError ? `Something went wrong: ${err.message}` : 'Callie is not answering right now — try again in a moment.' }]);
    } finally {
      setBusy(false);
    }
  };
  const toggleVoice = () => {
    setVoice((v) => {
      write(VOICE_KEY, v ? '0' : null);
      if (v) stopSpeaking();
      return !v;
    });
  };
  const toggleListening = () => {
    if (listening) {
      stopListening.current?.();
      return;
    }
    stopSpeaking();
    setListening(true);
    stopListening.current = listen(
      (interim) => setText(interim),
      (final) => {
        setText(final);
        void send(undefined, final);
      },
      () => {
        setListening(false);
        stopListening.current = null;
      },
    );
  };

  return (
    <div className={`callie-dock${open ? ' open' : ''}`}>
      {open && (
        <section className="callie-panel" aria-label="Callie">
          <header>
            <span className="av">C</span>
            <div>
              <strong>Callie</strong>
              <span className="muted small"> · your scheduling agent</span>
            </div>
            {voice && voices.length > 1 && (
              <select className="voice-pick" value={voiceName && voices.includes(voiceName) ? voiceName : voices[0]} onChange={(e) => { setVoiceName(e.target.value); write(VOICE_NAME_KEY, e.target.value); }} aria-label="Callie's voice" title="Callie's voice">
                {voices.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
            <button type="button" className={`voice-toggle${voice ? ' on' : ''}${speech === 'speaking' ? ' speaking' : ''}`} onClick={toggleVoice} aria-pressed={voice} title={voice ? 'Voice on — click to mute' : 'Voice off — click to hear replies'}>
              {voice ? '🔊' : '🔇'}
            </button>
            <button type="button" className="linklike" onClick={toggle} aria-label="Close">close</button>
          </header>
          <div className="callie-list" ref={listRef}>
            {msgs.length === 0 && <p className="muted small">{hint}</p>}
            {msgs.map((m, i) => (
              <div key={i} className={`bubble ${m.who === 'me' ? 'from-me' : 'from-callie'}`}>
                <div className="bubble-text">{m.text}</div>
                {m.actions && m.actions.some((a) => a.changed) && (
                  <div className="did">{m.actions.filter((a) => a.changed).map((a) => did(a)).join(' · ')}</div>
                )}
                {m.proposals?.map((p) => (
                  <ProposalCard key={p.id} proposal={p} editedBy={editedBy} onDone={(updated, note) => { setMsgs((all) => all.map((x) => (x === m ? { ...x, proposals: x.proposals?.map((q) => (q.id === updated.id ? updated : q)) } : x))); if (note) setMsgs((all) => [...all, { who: 'callie', text: note }]); }} />
                ))}
              </div>
            ))}
            {busy && <div className="bubble from-callie thinking">Callie is working on it…</div>}
          </div>
          <form className="callie-input" onSubmit={send}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder={listening ? 'Listening…' : 'Ask Callie…'}
              maxLength={4000}
              autoFocus
              disabled={busy}
            />
            {canListen() && (
              <button type="button" className={`mic${listening ? ' live' : ''}`} onClick={toggleListening} disabled={busy} aria-pressed={listening} aria-label={listening ? 'Stop listening' : 'Talk to Callie'} title={listening ? 'Stop listening' : 'Talk to Callie'}>
                🎤
              </button>
            )}
            <button type="submit" className="primary" disabled={busy || !text.trim()}>Send</button>
          </form>
          <p className="callie-foot muted">Callie works inside the rules: anything she changes is checked and recorded like any other change.</p>
        </section>
      )}
      <button type="button" className="callie-fab" onClick={toggle} aria-expanded={open} aria-label={open ? 'Close Callie' : 'Ask Callie'}>
        <span className="av">C</span>{!open && <span>Ask Callie</span>}
      </button>
    </div>
  );
}

/**
 * The scheduler's click. Nothing changes until "Apply"; if the preview found
 * errors, applying needs an override reason — the same two-step as the grid.
 * The reason is the scheduler's own words and lands in the audit row.
 */
function ProposalCard({ proposal, editedBy, onDone }: { proposal: CallieProposal; editedBy: string | null; onDone: (p: CallieProposal, note?: string) => void }) {
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const blocked = proposal.status === 'blocked' || proposal.blocked.length > 0;
  const needsOverride = !blocked && proposal.introduced.length > 0;
  const open = proposal.status === 'proposed' && !blocked;
  const apply = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const updated = await api.applyProposal(proposal.id, { reason: reason.trim(), ...(needsOverride ? { overrideReason: override.trim() } : {}), editedBy });
      onDone(updated, `Applied: ${proposal.summary}`);
      announceChange({ tools: ['apply_proposal'] });
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const dismiss = async () => {
    setBusy(true);
    try {
      onDone(await api.dismissProposal(proposal.id));
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`proposal ${proposal.status}`}>
      <div className="proposal-head"><span className="pill tiny">{proposal.kind}</span> <strong>{proposal.summary}</strong></div>
      {blocked ? (
        <div className="proposal-blocked">
          <ul className="proposal-errors">{(proposal.blocked.length ? proposal.blocked : proposal.introduced).map((e, i) => <li key={i}>{e}</li>)}</ul>
          <p className="small"><strong>Not allowed.</strong> A physician can only be scheduled at a hospital where they are credentialed — there is no override. Credential them on the Roster page first, then ask again.</p>
        </div>
      ) : needsOverride ? (
        <ul className="proposal-errors">{proposal.introduced.map((e, i) => <li key={i}>{e}</li>)}</ul>
      ) : (
        <p className="ok small">No new errors on the week.</p>
      )}
      {blocked ? null : open ? (
        <>
          <label className="field small"><span>Reason (recorded with the change)</span><input value={reason} maxLength={500} placeholder="e.g. sick day swap" onChange={(e) => setReason(e.target.value)} /></label>
          {needsOverride && <label className="field small"><span>Override reason (why apply despite the error)</span><input value={override} maxLength={500} onChange={(e) => setOverride(e.target.value)} /></label>}
          {problem && <p className="error small">{problem}</p>}
          <div className="formactions">
            <button type="button" className="primary" disabled={busy || !reason.trim() || (needsOverride && !override.trim())} onClick={apply}>{busy ? 'Applying…' : needsOverride ? 'Apply with override' : 'Apply'}</button>
            <button type="button" disabled={busy} onClick={dismiss}>Dismiss</button>
          </div>
        </>
      ) : (
        <p className={`small ${proposal.status === 'applied' ? 'ok' : 'muted'}`}>{proposal.status === 'applied' ? '✓ Applied' : proposal.status === 'dismissed' ? 'Dismissed' : `Not applied: ${proposal.outcome ?? proposal.status}`}</p>
      )}
    </div>
  );
}

function did(a: CallieAction): string {
  switch (a.tool) {
    case 'set_availability':
      return `Saved: week of ${String(a.input.week ?? '')} → ${String(a.input.state ?? '').replace('_', ' ')}`;
    case 'save_trip_answers':
      return `Saved your travel & lodging answers`;
    case 'add_residence':
      return `Added residence: ${String(a.input.name ?? '')}`;
    case 'add_room':
      return `Added room: ${String(a.input.name ?? '')}`;
    case 'assign_room':
      return `Assigned ${String(a.input.physician ?? '')} a room, ${String(a.input.arriveOn ?? '')} → ${String(a.input.departOn ?? '')}`;
    case 'propose_rooms':
      return `Proposed rooms for the week`;
    case 'confirm_room':
      return `Confirmed a room assignment`;
    case 'remove_room_assignment':
      return `Removed a room assignment`;
    default:
      return `Did: ${a.tool.replace(/_/g, ' ')}`;
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string | null) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* fine */
  }
}
